// docs/147 PG4: the air's particles (rain, snow, grit, fireflies, petals, leaves …) were spawned over
// the whole board, as many as the board is large: a snow on an exploration for twenty kept ~27,000
// flakes alive, every one moved and faded each frame (37 ms of a frame's CPU here), when a few
// thousand can be on screen. Games spawn weather round the camera; this does it without the tells.
//
// Each particle drifts at a speed fixed when it is born, so the only particles that can be in the
// camera's view are those born upwind of it, within one lifetime's drift. An emitter now spawns only
// over that stretch (`S`: the view swept back along the drift, plus a margin, within the zone), at
// the same rate per square pixel. What is alive is always exactly what whole-board spawning would
// have alive from the points of `S`: when the view moves past `S`, the particles born outside the
// new stretch are dropped (none of them can reach the view), and those born in the part just taken
// in are added as whole-board spawning would have them at that moment — at every age (lifetimes
// weighted by their length, ages even across them), each carried along its own drift. So whatever
// the camera does, every particle that can be on screen is there, and none that whole-board spawning
// would not have.
import Phaser from 'phaser';
import { VIEW_MARGIN } from './viewRender';
import { area, holds, inRect, meet, subtract, type Rect } from './rects';

export type { Rect } from './rects';

type Range = { min: number; max: number };
type Cfg = Phaser.Types.GameObjects.Particles.ParticleEmitterConfig;

/** Extra px round the drift: sway ops, a particle's own size, rounding. */
const PAD = 48;

const range = (v: unknown, fallback: number): Range | undefined => {
  if (v === undefined) return { min: fallback, max: fallback };
  if (typeof v === 'number') return { min: v, max: v };
  if (v && typeof v === 'object' && 'min' in v && 'max' in v && typeof (v as Range).min === 'number' && typeof (v as Range).max === 'number') return v as Range;
  return undefined;
};

/** What the camera shows, in board px (unclamped). */
function viewOf(cam: Phaser.Cameras.Scene2D.Camera): Rect {
  const ox = cam.width * cam.originX, oy = cam.height * cam.originY;
  return { x: cam.scrollX + ox - ox / cam.zoomX, y: cam.scrollY + oy - oy / cam.zoomY, w: cam.width / cam.zoomX, h: cam.height / cam.zoomY };
}

/**
 * One emitter that spawns uniformly over `zone` at a constant rate, spawned instead only where its
 * particles can reach the view. Build with `ViewZone.wrap`; call `update()` each frame after the
 * camera has moved and before the frame is drawn.
 */
export class ViewZone {
  private emitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  /** Where particles are born now. */
  private S: Rect = { x: 0, y: 0, w: 0, h: 0 };
  /** Where the zone picks birth points: `S`, or the part being filled. */
  private spawn: Rect[] = [];
  /** Spawns per ms per board px² (whole-board rate over the zone's area). */
  private readonly density: number;

  private constructor(private readonly cam: Phaser.Cameras.Scene2D.Camera, private readonly zone: Rect, private readonly vx: Range, private readonly vy: Range,
    private readonly life: Range, frequency: number, private readonly quantity: number) {
    this.density = quantity / (frequency * area(zone));
    this.S = this.need(viewOf(cam), VIEW_MARGIN);
    this.spawn = [this.S];
  }

  /**
   * `cfg` with its spawning moved to the view, and the zone that drives it — or undefined when the
   * emitter's motion is not a fixed drift (gravity, acceleration, a radial speed, a target …): those
   * keep spawning over the whole zone.
   */
  static wrap(cam: Phaser.Cameras.Scene2D.Camera, zone: Rect, cfg: Cfg): { cfg: Cfg; zone: ViewZone } | undefined {
    const c = cfg as Record<string, unknown>;
    for (const k of ['speed', 'angle', 'accelerationX', 'accelerationY', 'gravityX', 'gravityY', 'moveToX', 'moveToY', 'bounce', 'maxVelocityX', 'maxVelocityY', 'delay', 'hold', 'deathZone', 'follow', 'radial']) if (c[k] !== undefined) return undefined;
    const vx = range(c.speedX, 0), vy = range(c.speedY, 0), life = range(c.lifespan, 1000);
    const frequency = typeof c.frequency === 'number' ? c.frequency : undefined, quantity = c.quantity === undefined ? 1 : typeof c.quantity === 'number' ? c.quantity : undefined;
    if (!vx || !vy || !life || !frequency || frequency <= 0 || !quantity || area(zone) === 0 || c.emitting === false) return undefined;
    const z = new ViewZone(cam, zone, vx, vy, life, frequency, quantity);
    const own = cfg.emitCallback;
    return {
      cfg: {
        ...cfg, emitZone: { type: 'random', source: z.source }, frequency: z.rate(),
        // each particle remembers where it was born (where the zone put it: these drift from there)
        emitCallback: (p: Phaser.GameObjects.Particles.Particle, e: Phaser.GameObjects.Particles.ParticleEmitter) => { const b = p as Born; b.bornX = p.x; b.bornY = p.y; own?.(p, e); },
      },
      zone: z,
    };
  }

  attach(emitter: Phaser.GameObjects.Particles.ParticleEmitter): void { this.emitter = emitter; }

  /** Where the particles that can be in view `v` were born: `v` swept back along the drift by a lifetime, padded, within the zone. */
  private need(v: Rect, slack: number): Rect {
    const L = this.life.max / 1000;
    const sx = v.w * slack, sy = v.h * slack;
    const x0 = v.x - Math.max(0, this.vx.max) * L - PAD - sx, x1 = v.x + v.w - Math.min(0, this.vx.min) * L + PAD + sx;
    const y0 = v.y - Math.max(0, this.vy.max) * L - PAD - sy, y1 = v.y + v.h - Math.min(0, this.vy.min) * L + PAD + sy;
    return meet({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 }, this.zone);
  }

  /** The emitter's frequency (ms per spawn) for spawning over `S` at the whole board's density. */
  private rate(): number { return area(this.S) > 0 ? this.quantity / (this.density * area(this.S)) : 1e12; }

  private readonly source = {
    getRandomPoint: (p: Phaser.Types.Math.Vector2Like): void => {
      const rs = this.spawn;
      let r = rs[0]!;
      if (rs.length > 1) {
        let k = Math.random() * rs.reduce((s, x) => s + area(x), 0);
        for (const x of rs) { r = x; k -= area(x); if (k <= 0) break; }
      }
      p.x = r.x + Math.random() * r.w; p.y = r.y + Math.random() * r.h;
    },
  };

  /** Follow the view: move `S` when the view needs births it lacks (or has grown far smaller). */
  update(): void {
    const e = this.emitter;
    if (!e || !e.active || !e.emitting) return;
    const v = viewOf(this.cam);
    const next = this.need(v, VIEW_MARGIN);
    if (holds(this.S, this.need(v, 0)) && area(this.S) <= 3 * Math.max(1, area(next))) return;
    const before = this.S;
    this.S = next;
    // drop those born outside the new stretch: none of them can reach the view
    const list = e as unknown as { alive: Born[]; dead: Born[] };
    let n = 0;
    for (const p of list.alive) { if (p.bornX === undefined || inRect(next, p.bornX, p.bornY!)) list.alive[n++] = p; else list.dead.push(p); }
    list.alive.length = n;
    // add those born in the part just taken in, as whole-board spawning would have them now
    this.fill(subtract(next, before));
    this.spawn = [this.S];
    // the next spawn no later than one gap at the new rate (a stretch that was empty waited "forever")
    const flow = e as unknown as { flowCounter: number };
    e.frequency = this.rate();
    flow.flowCounter = Math.min(flow.flowCounter, e.frequency);
  }

  /** The particles born in `rects` over the last lifetime that are still alive, each where its drift has carried it. */
  private fill(rects: Rect[]): void {
    const e = this.emitter!;
    const total = rects.reduce((s, r) => s + area(r), 0);
    if (total === 0) return;
    const { min, max } = this.life;
    // alive from a spawn point: its rate × the mean lifetime
    const expect = this.density * total * ((min + max) / 2);
    let count = Math.floor(expect);
    if (Math.random() < expect - count) count++;
    this.spawn = rects;
    const list = e as unknown as { alive: Born[] };
    const processors = (e as unknown as { getProcessors(): unknown }).getProcessors();
    for (let i = 0; i < count; i++) {
      const before = list.alive.length;
      const p = e.emitParticle(1);
      if (!p || list.alive.length === before) continue;
      // a lifetime as the living have them (longer ones are more often alive), an age even across it
      const L = Math.sqrt(min * min + Math.random() * (max * max - min * min)), age = Math.random() * L;
      p.life = L; p.lifeCurrent = L - age;
      p.x += (p.velocityX * age) / 1000; p.y += (p.velocityY * age) / 1000;
      // its look at that age, before it is drawn
      p.update(0, 0, processors as never);
    }
  }
}

/** A particle with where it was born. */
type Born = Phaser.GameObjects.Particles.Particle & { bornX?: number; bornY?: number };
